/**
 * WebGL2 fallback caps (D-053; style guide §3.9). The WebGL2 backend runs on ANGLE, whose D3D11 path compiles every
 * GLSL program with FXC on the main thread's critical path. The D-047 lighting stack (MaterialX noise in the rock
 * and hull shaders above all, then the 4-level 5×5 PCF clipmap, GTAO and god rays) took 30-50 s to compile there,
 * so WebGL2 renders a lean subset. WebGPU is never capped. The table lives in `webgl2.json`; every system reads
 * its own row through `webgl2CapsOf(renderer)` (null = WebGPU, no caps).
 */
import { z } from 'zod'
import type { WebGPURenderer } from 'three/webgpu'
import { QUALITY_PRESETS, type QualityPreset } from './contracts'
import webgl2Json from './webgl2.json'

export const webgl2CapsSchema = z.object({
  /** Hull and decal shading tier (materials.json `presets`) on every preset. */
  materialTier: z.enum(QUALITY_PRESETS as readonly ['low', 'medium', 'high']),
  /** Key-light shadow clipmap levels built into the shaders (the presets' counts are capped to it). */
  shadowLevels: z.number().int().min(1).max(4),
  /** `hardware`: one bilinear compare tap (2×2 PCF). `pcf5`: the WebGPU 9-tap 5×5 kernel. */
  shadowFilter: z.enum(['hardware', 'pcf5']),
  /** Screen-space AO (GTAO). */
  ao: z.boolean(),
  /** Screen-space god rays. */
  godRays: z.boolean(),
  /** Procedural crater, relief and grit detail in the asteroid shader (MaterialX Worley and fractal noise). */
  rockDetail: z.boolean(),
})

export type WebGL2Caps = z.infer<typeof webgl2CapsSchema>

let cached: WebGL2Caps | null = null

export function loadWebGL2Caps(): WebGL2Caps {
  return (cached ??= webgl2CapsSchema.parse(webgl2Json))
}

/** True only for a live WebGL2 backend (a renderer without a backend, as in unit tests, counts as WebGPU). */
export function isWebGL2(renderer: Pick<WebGPURenderer, 'backend'> | undefined): boolean {
  const backend = renderer?.backend as { isWebGPUBackend?: boolean } | undefined
  return backend !== undefined && backend.isWebGPUBackend !== true
}

/** The caps that apply to `renderer`: null on WebGPU. */
export function webgl2CapsOf(renderer: Pick<WebGPURenderer, 'backend'> | undefined): WebGL2Caps | null {
  return isWebGL2(renderer) ? loadWebGL2Caps() : null
}

/** The material tier a preset renders with under `caps`. */
export function materialTierFor(preset: QualityPreset, caps: WebGL2Caps | null): QualityPreset {
  return caps === null ? preset : caps.materialTier
}
